import { api } from '../api';
import type { ClaseModificacion, ModificacionEstado } from '../catalogs/campos-modificables';

/** Una modificación del archivo, ya clasificada por el server. */
export interface ModificacionItem {
  /** Presente en las novedades que vienen de un tramite ya guardado. */
  id?: string;
  fila: number;
  cuilArchivo: string;
  cuilNormalizado: string;
  campo: string;
  valorNuevoCrudo: string;
  estado: ModificacionEstado;
  motivoCodigo?: string;
  motivo?: string;
  meta?: Record<string, unknown>;
  asociadoId?: string;
  legajoId?: string;
  afiId?: string;
  esTitular?: boolean;
  valorAnterior?: string;
  valorNuevo?: string;
  renaper?: {
    consultadoAt: string;
    ok: boolean;
    valorRenaper?: string;
    sexoConsultado?: string;
    mensaje?: string;
  };
  /** Qué solicitud de Binary le corresponde. */
  operacionBinary?: string;
  /** La respuesta de Binary, cuando el trámite ya se despachó. */
  binary?: {
    enviadoAt: string;
    /** Id de la solicitud en Binary. Es con lo que se la busca allá. */
    solafiId?: number;
    /** `05-----T` = transformada (impactó). Otro valor = quedó sin aplicar. */
    vdaDrv?: string | null;
    ok: boolean;
    error?: string;
  };
  /** Cuándo se copió el valor al portal. Solo pasa si Binary confirmó. */
  espejadoEnMongoAt?: string;
}

export interface LoteResumen {
  id: string;
  nombreArchivo: string;
  clase: ClaseModificacion;
  estado: 'validado' | 'confirmado' | 'procesando' | 'procesado' | 'procesado_con_errores' | 'error';
  subidoPor: string;
  createdAt: string;
  confirmadoAt?: string;
  resumen: ResumenModificaciones;
  diagnostico: string[];
  /** Cuándo terminó el despacho a Binary. */
  finishedAt?: string;
  /** Por qué murió el despacho, si murió. */
  error?: string;
}

export interface ResumenModificaciones {
  filas?: number;
  novedades: number;
  aceptadas: number;
  sinCambio: number;
  soloConsulta?: number;
  rechazadas: number;
  errorInfra: number;
  aplicadas?: number;
  falladas?: number;
}

export interface Pagina<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface PreviewModificaciones {
  /** Id del tramite creado. Es lo que se usa para confirmar y para paginar. */
  loteId: string;
  /** `true` si hay mas novedades de las que vinieron en `items`. */
  hayMas: boolean;
  clase: ClaseModificacion;
  nombreArchivo: string;
  total: number;
  resumen: ResumenModificaciones;
  porEstado: Record<string, number>;
  /** Problemas de plataforma o del archivo. Se muestran siempre: nada en silencio. */
  diagnostico: string[];
  columnas: {
    identificador: boolean;
    campos: string[];
    otraClase: string[];
    fueraDeAlcance: string[];
    desconocidas: string[];
  };
  items: ModificacionItem[];
}

/** Valida el archivo y clasifica cada modificación. No escribe nada. */
export async function previewModificaciones(
  file: File,
  clase: ClaseModificacion,
): Promise<PreviewModificaciones> {
  const fd = new FormData();
  fd.append('file', file);
  const { data } = await api.post<PreviewModificaciones>('/modificaciones/preview', fd, {
    params: { clase },
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

/** Trámites de modificación, del más nuevo al más viejo (RF-015). */
export async function listarLotes(params: {
  page?: number;
  pageSize?: number;
  clase?: ClaseModificacion;
  estado?: string;
} = {}): Promise<Pagina<LoteResumen>> {
  const { data } = await api.get<Pagina<LoteResumen>>('/modificaciones/lotes', { params });
  return data;
}

export async function getLote(id: string): Promise<LoteResumen> {
  const { data } = await api.get<LoteResumen>(`/modificaciones/lotes/${id}`);
  return data;
}

/** Novedades de un trámite, paginadas. Con `soloErrores` trae las que hay que corregir. */
export async function listarNovedades(
  id: string,
  params: { page?: number; pageSize?: number; estado?: string; soloErrores?: boolean } = {},
): Promise<Pagina<ModificacionItem>> {
  const { data } = await api.get<Pagina<ModificacionItem>>(
    `/modificaciones/lotes/${id}/novedades`,
    { params },
  );
  return data;
}

/**
 * Confirma el trámite. Manda SOLO los ids a excluir: los datos ya están
 * validados y guardados en el server, así que el front no puede colar una
 * modificación que no pasó por la validación.
 */
export async function confirmarLote(id: string, excluirIds: string[]): Promise<LoteResumen> {
  const { data } = await api.post<LoteResumen>(`/modificaciones/lotes/${id}/confirmar`, {
    excluirIds,
  });
  return data;
}

/**
 * Corrige el valor de UNA modificación y la re-valida, sin volver a subir el
 * archivo. Devuelve la fila con su estado nuevo.
 */
export async function corregirNovedad(
  loteId: string,
  novedadId: string,
  valorNuevo: string,
): Promise<ModificacionItem> {
  const { data } = await api.post<ModificacionItem>(
    `/modificaciones/lotes/${loteId}/novedades/${novedadId}/corregir`,
    { valorNuevo },
  );
  return data;
}

/**
 * Reintenta el envío a Binary de lo que quedó sin mandar. Solo toma lo que
 * sigue en estado aceptada, así que no puede duplicar lo ya aplicado.
 */
export async function reintentarLote(loteId: string): Promise<LoteResumen> {
  const { data } = await api.post<LoteResumen>(`/modificaciones/lotes/${loteId}/reintentar`);
  return data;
}

/** Reporte .xlsx de la validación, con TODAS las modificaciones y su motivo. */
export async function descargarReporte(id: string): Promise<void> {
  await descargarXlsx(`/modificaciones/lotes/${id}/reporte`, `reporte_modificaciones_${id}.xlsx`);
}

/** Planilla para Administración con lo aceptado, por operación de Binary. */
export async function descargarArchivoBinary(id: string): Promise<void> {
  await descargarXlsx(
    `/modificaciones/lotes/${id}/archivo-binary`,
    `modificaciones_binary_${id}.xlsx`,
  );
}

/** Descarga genérica de un .xlsx del backend, respetando el nombre que manda. */
async function descargarXlsx(url: string, fallback: string): Promise<void> {
  const res = await api.get(url, { responseType: 'blob' });
  const cd = (res.headers['content-disposition'] as string | undefined) ?? '';
  const filename = /filename="?([^"]+)"?/i.exec(cd)?.[1] ?? fallback;
  const blob = new Blob([res.data], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}

/** Descarga la plantilla del flujo indicado (con instrucciones y anexos). */
export async function descargarPlantillaModificaciones(clase: ClaseModificacion): Promise<void> {
  const res = await api.get('/modificaciones/plantilla', {
    params: { clase },
    responseType: 'blob',
  });
  const cd = (res.headers['content-disposition'] as string | undefined) ?? '';
  const filename =
    /filename="?([^"]+)"?/i.exec(cd)?.[1] ?? `plantilla_modificaciones_${clase}.xlsx`;
  const blob = new Blob([res.data], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
